import * as R from 'ramda';
import { StackItem } from '../../../../interfaces/use-order-item-stack.types';
import {
  BuildCombinationKeyParams,
  GridCells,
  MergeSeedIntoCellsParams,
  ReconcileCombinationCellsParams,
  ReconcileStateParams,
  ResolveCellParams,
} from './gridStore.types';

export const buildCombinationKey = ({
  productColorId,
  availability,
  deliveryWindowId,
  orderId,
}: BuildCombinationKeyParams) =>
  `${productColorId ?? ''}.${availability}.${deliveryWindowId ?? ''}.${orderId ?? ''}`;

const getCellUniqueKey = (item: StackItem) => `${buildCombinationKey(item)}.${item.skuId ?? ''}`;

export const deduplicateStackByCell = (items: StackItem[]) => R.uniqBy(getCellUniqueKey, items);

const resolveCell = ({
  existing,
  skuId,
  quantity,
  orderItemId,
  pendingSkuIds,
}: ResolveCellParams): GridCells[string][string] => {
  if (pendingSkuIds.has(skuId) && existing) {
    return existing;
  }
  if (
    existing &&
    existing.quantity === quantity &&
    existing.orderItemId === orderItemId &&
    !existing.isPending
  ) {
    return existing;
  }
  return { quantity, isPending: false, orderItemId };
};

export const reconcileCombinationCells = ({
  combination,
  savedItems,
  pendingQuantitiesBySku,
}: ReconcileCombinationCellsParams): GridCells[string] => {
  let next = combination;

  savedItems.forEach((savedItem) => {
    const { skuId } = savedItem;
    if (!skuId) {
      return;
    }
    const quantity = savedItem.orderItemQuantity ?? 0;
    const pendingQuantity = pendingQuantitiesBySku.get(skuId);
    if (pendingQuantity !== undefined && pendingQuantity !== quantity) {
      return;
    }
    const existing = combination[skuId];
    const orderItemId = savedItem.orderItemId ?? existing?.orderItemId;
    if (
      existing &&
      existing.quantity === quantity &&
      existing.orderItemId === orderItemId &&
      !existing.isPending
    ) {
      return;
    }
    if (next === combination) {
      next = { ...combination };
    }
    next[skuId] = { quantity, isPending: false, orderItemId };
  });

  return next;
};

export const reconcileState = ({
  state,
  combinationKey,
  savedItems,
}: ReconcileStateParams): { cells: GridCells; pending: StackItem[] } | null => {
  const prevCombination = state.cells[combinationKey] ?? {};

  const pendingQuantitiesBySku = new Map<string, number>();
  state.pending.forEach((item) => {
    if (buildCombinationKey(item) !== combinationKey || !item.skuId) {
      return;
    }
    if (!pendingQuantitiesBySku.has(item.skuId)) {
      pendingQuantitiesBySku.set(item.skuId, item.quantity ?? 0);
    }
  });

  const nextCombination = reconcileCombinationCells({
    combination: prevCombination,
    savedItems,
    pendingQuantitiesBySku,
  });

  const nextPending = state.pending.filter((item) => {
    if (buildCombinationKey(item) !== combinationKey || !item.skuId) {
      return true;
    }
    const savedItem = savedItems.find((saved) => saved.skuId === item.skuId);
    if (!savedItem) {
      return true;
    }
    return (item.quantity ?? 0) !== (savedItem.orderItemQuantity ?? 0);
  });

  const hasCellChange = nextCombination !== prevCombination;
  const hasPendingChange = nextPending.length !== state.pending.length;

  if (!hasCellChange && !hasPendingChange) {
    return null;
  }

  return {
    cells: hasCellChange ? { ...state.cells, [combinationKey]: nextCombination } : state.cells,
    pending: hasPendingChange ? nextPending : state.pending,
  };
};

export const mergeSeedIntoCells = ({
  cells,
  combinationKey,
  grid,
  pendingSkuIds,
}: MergeSeedIntoCellsParams): GridCells => {
  const current = cells[combinationKey] ?? {};
  const nextCombination: Record<string, (typeof current)[string]> = {};

  grid.forEach((gridItem) => {
    const { skuId } = gridItem;
    if (!skuId) {
      return;
    }
    nextCombination[skuId] = resolveCell({
      existing: current[skuId],
      skuId,
      quantity: gridItem.orderItemQuantity ?? 0,
      orderItemId: gridItem.orderItemId,
      pendingSkuIds,
    });
  });

  const currentSkuIds = Object.keys(current);
  const nextSkuIds = Object.keys(nextCombination);
  const isUnchanged =
    currentSkuIds.length === nextSkuIds.length &&
    nextSkuIds.every((skuId) => current[skuId] === nextCombination[skuId]);

  if (isUnchanged) {
    return cells;
  }

  return { ...cells, [combinationKey]: nextCombination };
};
